import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Trash2, Check, X, Pencil, Upload, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { expandAccountShortcut, inferAccountLength } from "@/lib/accountShortcut";
import { kindForAccount, matchesGroupFilter, type AccountGroupFilter, type AccountKind } from "@/lib/unifiedAccounts";
import {
  OPERATION_TYPE_OPTIONS, RETENTION_TYPE_OPTIONS,
  normalizeOperationType, normalizeRetentionType,
  type SupplierOperationType, type SupplierRetentionType,
} from "@/lib/supplierMaster";
import { listCustomers, upsertCustomer, deleteCustomer, normalizeNif, type CustomerMasterRecord } from "@/lib/customerMaster";
import MasterImportDialog from "@/components/master/MasterImportDialog";
import MasterDuplicatesDialog from "@/components/master/MasterDuplicatesDialog";
import { findDuplicateGroups } from "@/lib/masterDuplicates";

interface SupplierRaw {
  id: string;
  account_code: string;
  description: string;
  tax_id: string;
  expense_account: string;
  operation_type?: string;
  retention_type?: string;
}

interface CatalogRaw {
  id: string;
  account: string;
  description: string;
}

interface Row {
  id: string;
  kind: AccountKind;
  account: string;
  description: string;
  taxId: string;
  counterpart: string;
}

interface EditState {
  kind: AccountKind;
  id: string;
  account: string;
  description: string;
  taxId: string;
  counterpart: string;
  operationType: SupplierOperationType;
  retentionType: SupplierRetentionType;
  postalCode: string;
}

const TABLE: Record<AccountKind, "supplier_master" | "customer_master" | "company_account_catalog"> = {
  proveedor: "supplier_master",
  cliente: "customer_master",
  general: "company_account_catalog",
};

const KIND_LABEL: Record<AccountKind, string> = { proveedor: "Proveedor", cliente: "Cliente", general: "General" };

const FILTERS: { value: AccountGroupFilter; label: string }[] = [
  { value: "todos", label: "Todas" },
  { value: "proveedores", label: "Proveedores 40/41" },
  { value: "clientes", label: "Clientes 43/44" },
  { value: "tesoreria", label: "Tesorería y puente 5" },
  { value: "gastos_ingresos", label: "Gastos e ingresos 6/7" },
  { value: "otros", label: "Otras" },
];

const fetchAll = async (table: string, select: string, companyId: string) => {
  const out: any[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await (supabase.from(table as any) as any)
      .select(select).eq("company_id", companyId).order("id").range(from, from + 999);
    if (error || !data?.length) break;
    out.push(...data);
    if (data.length < 1000) break;
  }
  return out;
};

interface Props {
  companyId: string;
  companyName?: string;
  onChanged?: () => void;
}

const UnifiedAccountsTab = ({ companyId, companyName, onChanged }: Props) => {
  const { toast } = useToast();
  const [supRows, setSupRows] = useState<SupplierRaw[]>([]);
  const [cusRows, setCusRows] = useState<(CustomerMasterRecord & { id: string })[]>([]);
  const [catRows, setCatRows] = useState<CatalogRaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<AccountGroupFilter>("todos");
  const [adding, setAdding] = useState(false);
  const [next, setNext] = useState({ account: "", description: "", taxId: "", counterpart: "" });
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  // Importación y duplicados de maestros (proveedores / clientes)
  const [masterKind, setMasterKind] = useState<"suppliers" | "customers">("suppliers");
  const [importOpen, setImportOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);

  const accountLength = useMemo(
    () => inferAccountLength([
      ...supRows.flatMap((r) => [r.account_code, r.expense_account]),
      ...cusRows.flatMap((c) => [c.accountCode, c.salesAccount]),
      ...catRows.map((r) => r.account),
    ]),
    [supRows, cusRows, catRows],
  );
  const expandAcc = (v: string) => expandAccountShortcut(v, accountLength);

  const reload = async () => {
    const [sup, cat, cus] = await Promise.all([
      fetchAll("supplier_master", "id, account_code, description, tax_id, expense_account, operation_type, retention_type", companyId),
      fetchAll("company_account_catalog", "id, account, description", companyId),
      listCustomers(companyId),
    ]);
    setSupRows((sup as unknown as SupplierRaw[]).map((r) => ({
      ...r,
      operation_type: normalizeOperationType(r.operation_type),
      retention_type: normalizeRetentionType(r.retention_type),
    })));
    setCatRows(cat as unknown as CatalogRaw[]);
    setCusRows(cus);
    setLoading(false);
  };

  useEffect(() => { setLoading(true); reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId]);

  const rows: Row[] = useMemo(() => [
    ...supRows.map((r) => ({ id: r.id, kind: "proveedor" as const, account: r.account_code, description: r.description, taxId: r.tax_id, counterpart: r.expense_account })),
    ...cusRows.map((r) => ({ id: r.id, kind: "cliente" as const, account: r.accountCode, description: r.description, taxId: r.taxId, counterpart: r.salesAccount })),
    ...catRows.map((r) => ({ id: r.id, kind: "general" as const, account: r.account, description: r.description, taxId: "", counterpart: "" })),
  ].sort((a, b) => a.account.localeCompare(b.account) || a.description.localeCompare(b.description)), [supRows, cusRows, catRows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => matchesGroupFilter(r.account, filter) && (!q ||
      r.account.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) ||
      r.taxId.toLowerCase().includes(q) || r.counterpart.toLowerCase().includes(q)));
  }, [rows, search, filter]);

  const nextKind = kindForAccount(next.account);
  const needsTax = nextKind !== "general";

  const dupGroups = useMemo(
    () => masterKind === "suppliers"
      ? findDuplicateGroups(supRows.map((r) => ({ id: r.id, taxId: r.tax_id, account: r.account_code, description: r.description })))
      : findDuplicateGroups(cusRows.map((r) => ({ id: r.id, taxId: r.taxId, account: r.accountCode, description: r.description }))),
    [masterKind, supRows, cusRows],
  );

  const save = async () => {
    const account = expandAccountShortcut(next.account.trim(), accountLength);
    const description = next.description.trim();
    if (!account || !description) { toast({ title: "Indica cuenta y descripción", variant: "destructive" }); return; }
    if (rows.some((r) => r.account === account)) { toast({ title: `La cuenta ${account} ya existe`, variant: "destructive" }); return; }
    const kind = kindForAccount(account);
    if (kind !== "general" && !next.taxId.trim()) { toast({ title: "Indica el NIF/CIF", variant: "destructive" }); return; }
    const tax = next.taxId.trim().toUpperCase();
    const cp = next.counterpart.trim() ? expandAccountShortcut(next.counterpart.trim(), accountLength) : "";
    const payload =
      kind === "proveedor" ? { company_id: companyId, account_code: account, description, tax_id: normalizeNif(tax), expense_account: cp, operation_type: "interior", retention_type: "none" }
      : kind === "cliente" ? { company_id: companyId, account_code: account, description, tax_id: normalizeNif(tax), sales_account: cp, operation_type: "interior" }
      : { company_id: companyId, account, description };
    const { error } = await (supabase.from(TABLE[kind]) as any).insert(payload);
    if (error) { toast({ title: "No se pudo crear la cuenta", description: error.message, variant: "destructive" }); return; }
    toast({ title: `Cuenta ${account} creada (${KIND_LABEL[kind]})` });
    setNext({ account: "", description: "", taxId: "", counterpart: "" });
    setAdding(false);
    await reload();
    onChanged?.();
  };

  const remove = async (r: Row) => {
    if (!confirm(`¿Eliminar la cuenta ${r.account} · ${r.description}?`)) return;
    let error: { message: string } | null = null;
    if (r.kind === "cliente") {
      const res = await deleteCustomer(r.id);
      error = res.error ? { message: res.error } : null;
    } else {
      const res = await (supabase.from(TABLE[r.kind]) as any).delete().eq("id", r.id);
      error = res.error ?? null;
    }
    if (error) { toast({ title: "No se pudo eliminar", description: error.message, variant: "destructive" }); return; }
    await reload();
    onChanged?.();
  };

  const openEdit = (r: Row) => {
    if (r.kind === "proveedor") {
      const raw = supRows.find((s) => s.id === r.id);
      if (!raw) return;
      setEditing({
        kind: "proveedor", id: r.id, account: raw.account_code, description: raw.description,
        taxId: raw.tax_id, counterpart: raw.expense_account || "",
        operationType: normalizeOperationType(raw.operation_type),
        retentionType: normalizeRetentionType(raw.retention_type),
        postalCode: "",
      });
    } else if (r.kind === "cliente") {
      const raw = cusRows.find((c) => c.id === r.id);
      if (!raw) return;
      setEditing({
        kind: "cliente", id: r.id, account: raw.accountCode, description: raw.description,
        taxId: raw.taxId, counterpart: raw.salesAccount || "",
        operationType: raw.operationType, retentionType: "none",
        postalCode: raw.postalCode || "",
      });
    } else {
      const raw = catRows.find((c) => c.id === r.id);
      if (!raw) return;
      setEditing({
        kind: "general", id: r.id, account: raw.account, description: raw.description,
        taxId: "", counterpart: "", operationType: "interior", retentionType: "none", postalCode: "",
      });
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    const account = expandAcc(editing.account.trim());
    const description = editing.description.trim();
    if (!account || !description) {
      toast({ title: "Indica cuenta y descripción", variant: "destructive" });
      setSaving(false);
      return;
    }
    if (rows.some((r) => r.kind === editing.kind && r.id !== editing.id && r.account === account)) {
      toast({ title: `La cuenta ${account} ya existe`, variant: "destructive" });
      setSaving(false);
      return;
    }
    let errMsg: string | undefined;
    if (editing.kind === "proveedor") {
      const { error } = await supabase.from("supplier_master").update({
        account_code: account,
        description,
        tax_id: normalizeNif(editing.taxId.trim().toUpperCase()),
        expense_account: editing.counterpart.trim() ? expandAcc(editing.counterpart.trim()) : "",
        operation_type: editing.operationType,
        retention_type: editing.retentionType,
      }).eq("id", editing.id);
      errMsg = error?.message;
    } else if (editing.kind === "cliente") {
      const res = await upsertCustomer(companyId, {
        id: editing.id,
        accountCode: account,
        salesAccount: editing.counterpart.trim() ? expandAcc(editing.counterpart.trim()) : "",
        description,
        taxId: editing.taxId.trim().toUpperCase(),
        operationType: editing.operationType,
        postalCode: editing.postalCode.replace(/\D/g, "").slice(0, 5) || undefined,
      });
      errMsg = res.error;
    } else {
      const { error } = await supabase.from("company_account_catalog").update({
        account, description,
      }).eq("id", editing.id);
      errMsg = error?.message;
    }
    setSaving(false);
    if (errMsg) {
      toast({ title: "Error al guardar", description: errMsg, variant: "destructive" });
      return;
    }
    toast({ title: editing.kind === "proveedor" ? "Proveedor actualizado" : editing.kind === "cliente" ? "Cliente actualizado" : "Cuenta actualizada" });
    setEditing(null);
    await reload();
    onChanged?.();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar por cuenta, nombre o NIF..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={masterKind} onValueChange={(v) => setMasterKind(v as "suppliers" | "customers")}>
          <SelectTrigger className="w-36 h-9 text-xs" aria-label="Maestro para importar o depurar">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="suppliers" className="text-xs">Proveedores</SelectItem>
            <SelectItem value="customers" className="text-xs">Clientes</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2">
          <Upload className="h-4 w-4" /> Importar Excel
        </Button>
        <Button variant="outline" onClick={() => setDupOpen(true)} className="gap-2">
          <Copy className="h-4 w-4" /> Duplicados por NIF ({dupGroups.length})
        </Button>
        <Button onClick={() => setAdding(true)} disabled={adding} className="gap-2 ml-auto">
          <Plus className="h-4 w-4" /> Nueva cuenta
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button key={f.value} size="sm" variant={filter === f.value ? "default" : "outline"} onClick={() => setFilter(f.value)}>
            {f.label} ({rows.filter((r) => matchesGroupFilter(r.account, f.value)).length})
          </Button>
        ))}
      </div>

      <MasterDuplicatesDialog
        open={dupOpen}
        onOpenChange={setDupOpen}
        table={masterKind === "suppliers" ? "supplier_master" : "customer_master"}
        label={masterKind === "suppliers" ? "proveedores" : "clientes"}
        groups={dupGroups}
        onDone={async () => { await reload(); onChanged?.(); }}
      />

      {companyName !== undefined && (
        <MasterImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          kind={masterKind}
          companyId={companyId}
          companyName={companyName}
          accountLength={accountLength}
          onImported={async () => { await reload(); onChanged?.(); }}
        />
      )}

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs w-32">Subcuenta</TableHead>
              <TableHead className="text-xs">Descripción / Razón social</TableHead>
              <TableHead className="text-xs w-32">NIF/CIF</TableHead>
              <TableHead className="text-xs w-32">Contrapartida</TableHead>
              <TableHead className="text-xs w-28">Tipo</TableHead>
              <TableHead className="text-xs w-28 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adding && (
              <TableRow>
                <TableCell><Input autoFocus value={next.account} onChange={(e) => setNext({ ...next, account: e.target.value })} placeholder="555.1" className="h-8 text-xs font-mono" /></TableCell>
                <TableCell><Input value={next.description} onChange={(e) => setNext({ ...next, description: e.target.value })} placeholder="Descripción" className="h-8 text-xs" /></TableCell>
                <TableCell><Input value={next.taxId} disabled={!needsTax} onChange={(e) => setNext({ ...next, taxId: e.target.value })} placeholder={needsTax ? "NIF/CIF" : "—"} className="h-8 text-xs font-mono" /></TableCell>
                <TableCell><Input value={next.counterpart} disabled={!needsTax} onChange={(e) => setNext({ ...next, counterpart: e.target.value })} placeholder={needsTax ? (nextKind === "cliente" ? "700" : "6xx") : "—"} className="h-8 text-xs font-mono" /></TableCell>
                <TableCell><Badge variant="secondary">{next.account ? KIND_LABEL[nextKind] : "—"}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" aria-label="Guardar" onClick={save}><Check className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" aria-label="Cancelar" onClick={() => setAdding(false)}><X className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            )}
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">Cargando…</TableCell></TableRow>
            ) : visible.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No hay cuentas</TableCell></TableRow>
            ) : visible.slice(0, 1000).map((r) => (
              <TableRow key={`${r.kind}-${r.id}`}>
                <TableCell className="text-xs font-mono">{r.account}</TableCell>
                <TableCell className="text-xs">{r.description}</TableCell>
                <TableCell className="text-xs font-mono">{r.taxId}</TableCell>
                <TableCell className="text-xs font-mono">{r.counterpart}</TableCell>
                <TableCell><Badge variant={r.kind === "general" ? "outline" : "secondary"}>{KIND_LABEL[r.kind]}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" aria-label="Editar ficha" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" aria-label="Eliminar" onClick={() => remove(r)}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {visible.length > 1000 && <p className="text-xs text-muted-foreground">Mostrando 1.000 de {visible.length}. Usa el buscador para acotar.</p>}

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? `Ficha ${KIND_LABEL[editing.kind]} · ${editing.account}` : ""}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Subcuenta</label>
                <Input value={editing.account} onChange={(e) => setEditing({ ...editing, account: e.target.value })} className="h-8 text-xs font-mono" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Descripción / Razón social</label>
                <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="h-8 text-xs" />
              </div>
              {editing.kind !== "general" && (
                <>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">NIF/CIF</label>
                    <Input value={editing.taxId} onChange={(e) => setEditing({ ...editing, taxId: e.target.value })} className="h-8 text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{editing.kind === "cliente" ? "Cuenta de venta" : "Contrapartida (gasto)"}</label>
                    <Input value={editing.counterpart} onChange={(e) => setEditing({ ...editing, counterpart: e.target.value })} placeholder={editing.kind === "cliente" ? "70000000" : "60700000"} className="h-8 text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Tipo de operación IVA</label>
                    <Select value={editing.operationType} onValueChange={(v) => setEditing({ ...editing, operationType: v as SupplierOperationType })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OPERATION_TYPE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} className="text-xs">{o.short}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {editing.kind === "proveedor" ? (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Retención IRPF</label>
                      <Select value={editing.retentionType} onValueChange={(v) => setEditing({ ...editing, retentionType: v as SupplierRetentionType })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {RETENTION_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value} className="text-xs">{o.short}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Código postal</label>
                      <Input value={editing.postalCode} onChange={(e) => setEditing({ ...editing, postalCode: e.target.value.replace(/\D/g, "").slice(0, 5) })} placeholder="28001" className="h-8 text-xs font-mono" />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button size="sm" onClick={saveEdit} disabled={saving} className="gap-2">
              <Check className="h-4 w-4" /> Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedAccountsTab;
